import { useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Crop } from "@/types/crop";
import { createCrop } from "@/services/firebase";
import { useToast } from "@/hooks/use-toast";

interface CropSelectorProps {
  crops: Crop[];
  cropsLoading: boolean;
  selectedCrops: string[];
  onToggleCrop: (cropName: string) => void;
  canAddCrop?: boolean;
  initialOrphanCrops?: string[]; // Orphan crops to always show (persist even after deselection)
}

export const CropSelector: React.FC<CropSelectorProps> = ({
  crops,
  cropsLoading,
  selectedCrops,
  onToggleCrop,
  canAddCrop = true,
  initialOrphanCrops = [],
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { toast } = useToast();
  const commandRef = useRef<HTMLDivElement | null>(null);

  // Sort crops alphabetically
  const activeCrops = useMemo(() =>
    [...crops].sort((a, b) => a.name.localeCompare(b.name)),
    [crops]
  );

  // Combine initialOrphanCrops with any currently selected orphans
  // These persist in the list even after being deselected
  const allOrphanCrops = useMemo(() => {
    const orphansFromSelection = selectedCrops.filter(
      cropName => !activeCrops.some(c => c.name === cropName)
    );
    const combined = new Set([...initialOrphanCrops, ...orphansFromSelection]);
    return Array.from(combined).sort((a, b) => a.localeCompare(b));
  }, [selectedCrops, activeCrops, initialOrphanCrops]);

  const getExactMatchName = (name: string) => {
    const normalized = name.trim().toLowerCase();
    if (!normalized) return null;
    const activeMatch = activeCrops.find(
      crop => crop.name.toLowerCase() === normalized
    )?.name;
    if (activeMatch) return activeMatch;
    const orphanMatch = allOrphanCrops.find(
      cropName => cropName.toLowerCase() === normalized
    );
    return orphanMatch ?? null;
  };

  const handleCreateCrop = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;

    const existingName = getExactMatchName(trimmed);
    if (existingName) {
      if (!selectedCrops.includes(existingName)) {
        onToggleCrop(existingName);
      }
      setSearchValue("");
      return;
    }

    if (!canAddCrop) {
      toast({
        title: "Permission required",
        description: "Only editors and admins can add new crops.",
        variant: "destructive",
      });
      return;
    }

    try {
      const created = await createCrop({ name: trimmed, description: "" });
      if (created) {
        toast({
          title: "Crop added",
          description: `${trimmed} has been added successfully`,
        });
        if (!selectedCrops.includes(trimmed)) {
          // Auto-select the newly created crop
          onToggleCrop(trimmed);
        }
        setSearchValue("");
      } else {
        toast({
          title: "Error",
          description: "Failed to add crop",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error adding crop:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add crop",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div className="space-y-2">
        {selectedCrops.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedCrops.map((cropName) => (
              <Badge
                key={cropName}
                variant="outline"
                className="border-agri-green/40 bg-agri-green-light/10 text-sm text-foreground"
              >
                <span>{cropName}</span>
                <button
                  type="button"
                  onClick={() => onToggleCrop(cropName)}
                  className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label={`Remove ${cropName}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {cropsLoading ? (
          <Button
            variant="outline"
            className="w-full justify-between"
            disabled
          >
            <span className="text-muted-foreground">Loading crops...</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        ) : (
          <Popover
            open={open}
            onOpenChange={(nextOpen) => {
              setOpen(nextOpen);
              if (!nextOpen) {
                setSearchValue("");
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full justify-between"
              >
                {selectedCrops.length === 0 ? (
                  <span className="text-muted-foreground">Select crops</span>
                ) : (
                  <span>
                    {selectedCrops.length} crop{selectedCrops.length !== 1 ? "s" : ""} selected
                  </span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command ref={commandRef}>
                <CommandInput
                  placeholder={canAddCrop ? "Search or add crops..." : "Search crops..."}
                  value={searchValue}
                  onValueChange={setSearchValue}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    const trimmed = searchValue.trim();
                    if (!trimmed) return;
                    const selectedItem = commandRef.current?.querySelector(
                      "[cmdk-item][data-selected='true'], [cmdk-item][aria-selected='true']"
                    ) as HTMLElement | null;
                    if (selectedItem && selectedItem.dataset.addItem !== "true") {
                      return;
                    }
                    const exactMatch = getExactMatchName(trimmed);
                    if (exactMatch) {
                      event.preventDefault();
                      if (!selectedCrops.includes(exactMatch)) {
                        onToggleCrop(exactMatch);
                      }
                      setSearchValue("");
                      return;
                    }
                    if (!canAddCrop) return;
                    event.preventDefault();
                    void handleCreateCrop(trimmed);
                  }}
                />
                <CommandList>
                  <CommandEmpty>No crops found.</CommandEmpty>
                  <CommandGroup>
                    {allOrphanCrops.map((cropName) => {
                      const isSelected = selectedCrops.includes(cropName);
                      return (
                        <CommandItem
                          key={`orphan-${cropName}`}
                          value={cropName}
                          onSelect={() => onToggleCrop(cropName)}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              isSelected ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {cropName}
                        </CommandItem>
                      );
                    })}
                    {activeCrops.map((crop) => {
                      const isSelected = selectedCrops.includes(crop.name);
                      return (
                        <CommandItem
                          key={crop.id}
                          value={crop.name}
                          onSelect={() => onToggleCrop(crop.name)}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              isSelected ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {crop.name}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                  {canAddCrop && searchValue.trim() && !getExactMatchName(searchValue) && (
                    <>
                      <CommandSeparator />
                      <CommandGroup>
                        <CommandItem
                          value={`add-${searchValue}`}
                          data-add-item="true"
                          onSelect={() => {
                            void handleCreateCrop(searchValue);
                          }}
                          className="text-agri-green"
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add "{searchValue.trim()}"
                        </CommandItem>
                      </CommandGroup>
                    </>
                  )}
                  {!canAddCrop && (
                    <>
                      <CommandSeparator />
                      <CommandGroup>
                        <CommandItem disabled className="text-muted-foreground">
                          Only editors and admins can add new crops.
                        </CommandItem>
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </>
  );
};

export default CropSelector;
